import React from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';

interface TimeDisplayProps {
  /** 引取開始時刻 */
  pickupTimeStart: string;
  /** 引取終了時刻（オプション） */
  pickupTimeEnd?: string | null;
  /** 搬入時刻（オプション） */
  deliveryTime?: string | null;
  /** テキストサイズ（デフォルト: text-xs） */
  textSize?: 'text-xs' | 'text-sm';
  /** 縦方向のスペーシング（デフォルト: space-y-0.5） */
  spacing?: 'space-y-0.5' | 'space-y-1';
}

/**
 * HH:MM形式の時刻をISO 8601形式（HH:MM:SS）に変換
 * @param time - HH:MM形式の時刻文字列
 * @returns HH:MM:SS形式の時刻文字列。不正な形式の場合は00:00:00を返す
 */
const formatTimeForDateTime = (time: string | null | undefined): string => {
  // 1. null/undefinedチェック
  if (!time || typeof time !== 'string') {
    return '00:00:00';
  }

  const parts = time.split(':');

  // 2. すでにHH:MM:SS形式の場合はそのまま返す
  if (parts.length === 3) {
    return time;
  }

  // 3. HH:MM形式の場合は:00を追加（簡易バリデーション付き）
  if (parts.length === 2) {
    const [hours, minutes] = parts;
    // HH:MM形式の基本チェック（2桁ずつ）
    if (hours.length === 2 && minutes.length === 2) {
      return `${time}:00`;
    }
  }

  // 4. 不正な形式の場合
  console.warn(`[TimeDisplay] Invalid time format: "${time}"`);
  return '00:00:00';
};

/**
 * 案件の時間情報を表示する共通コンポーネント
 * 引取時間と搬入時間を統一フォーマットで表示
 */
export const TimeDisplay: React.FC<TimeDisplayProps> = ({
  pickupTimeStart,
  pickupTimeEnd,
  deliveryTime,
  textSize = 'text-xs',
  spacing = 'space-y-0.5',
}) => {
  // 引取時間の表示テキストを生成
  const pickupTimeText = (() => {
    if (!pickupTimeEnd || pickupTimeEnd === pickupTimeStart) {
      return pickupTimeStart;
    }
    return `${pickupTimeStart} - ${pickupTimeEnd}`;
  })();

  return (
    <div className={spacing}>
      {/* 引取時間 */}
      <div className={`flex items-center gap-1 ${textSize}`}>
        <ClockIcon className='h-3 w-3 text-blue-500 flex-shrink-0' aria-hidden='true' />
        <span className='font-medium text-gray-900 dark:text-white'>
          引取: <time dateTime={formatTimeForDateTime(pickupTimeStart)}>{pickupTimeText}</time>
        </span>
      </div>
      {/* 搬入時間 */}
      <div className={`flex items-center gap-1 ${textSize}`}>
        <ClockIcon className='h-3 w-3 text-green-500 flex-shrink-0' aria-hidden='true' />
        <span className='font-medium text-gray-900 dark:text-white'>
          搬入:{' '}
          {deliveryTime ? (
            <time dateTime={formatTimeForDateTime(deliveryTime)}>{deliveryTime}</time>
          ) : (
            '未定'
          )}
        </span>
      </div>
    </div>
  );
};
